import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { archiveroService } from '../services/archiveroService';
import api from '../services/api';
import { 
  Download, Search, Archive, Eye, 
  Paperclip, ChevronDown, ChevronUp, DollarSign, FileText, Upload,
  MessageSquare, Send
} from 'lucide-react';
import toast from 'react-hot-toast';
import Pagination from '../components/Pagination.jsx';
import SubirDocumentosModal from '../components/SubirDocumentosModal.jsx';

export default function GestionArchivero() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');

  useEffect(() => {
    const s = searchParams.get('search');
    if (s !== null) {
      setSearchTerm(s);
    }
  }, [searchParams]);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [mostrarSoloActivas, setMostrarSoloActivas] = useState(true);
  const [mostrarSoloMias, setMostrarSoloMias] = useState(false);
  const [estados, setEstados] = useState([]);
  const [respuestas, setRespuestas] = useState({});
  const [enviandoMensaje, setEnviandoMensaje] = useState({});
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  const [expandedId, setExpandedId] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState({});

  useEffect(() => {
    fetchSolicitudes();
    fetchEstados();
  }, []);

  const fetchEstados = async () => {
    try {
      const { data } = await api.get('/estados?tipo=archivero');
      setEstados(data);
    } catch {
      // Ignorar
    }
  };

  const fetchSolicitudes = async () => {
    setLoading(true);
    try {
      const { data } = await archiveroService.getSolicitudes({});
      setSolicitudes(data);
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar solicitudes de archivero');
    } finally {
      setLoading(false);
    }
  };

  const getBadgeClass = (estadoNombre) => {
    const txt = (estadoNombre || '').toLowerCase();
    if (txt.includes('pendiente')) return 'badge-warning';
    if (txt.includes('pagado')) return 'badge-success';
    if (txt.includes('completado') || txt.includes('confirmado') || txt.includes('recibido')) return 'badge-success';
    if (txt.includes('rechazad')) return 'badge-error';
    return 'badge-info';
  };

  const cargarDetalle = async (solicitud) => {
    try {
      const { data } = await api.get(`/archivero/solicitudes/${solicitud.id}`);
      setExpandedDetail(prev => ({ ...prev, [solicitud.id]: data }));
    } catch {
      setExpandedDetail(prev => ({ ...prev, [solicitud.id]: solicitud }));
    }
  };

  const toggleExpand = (solicitud) => {
    if (expandedId === solicitud.id) {
      setExpandedId(null);
    } else {
      setExpandedId(solicitud.id);
      cargarDetalle(solicitud);
    }
  };

  const [usuarios, setUsuarios] = useState([]);
  const [modalSubirDocs, setModalSubirDocs] = useState(null);
  const [canAssign, setCanAssign] = useState(false);

  useEffect(() => {
    fetchUsuarios();
    const role = localStorage.getItem('role') || '';
    setCanAssign(['super_admin', 'admin', 'boss'].includes(role));
  }, []);

  const fetchUsuarios = async () => {
    try {
      const { data } = await api.get('/users/assignable');
      setUsuarios(data.data || []);
    } catch {
      // Ignorar error si no se pueden cargar
    }
  };

  const cambiarEstado = async (id, estado_id) => {
    try {
      await api.patch(`/archivero/solicitudes/${id}/estado`, { estado_id });
      toast.success('Estado actualizado');
      fetchSolicitudes();
      if (expandedId === id) {
        cargarDetalle({ id });
      }
    } catch {
      toast.error('Error al actualizar estado');
    }
  };

  const handleAsignar = async (id, worker_id) => {
    try {
      await api.patch(`/archivero/solicitudes/${id}/asignar`, { worker_id: worker_id || null });
      toast.success(worker_id ? 'Solicitud asignada.' : 'Asignación removida.');
      fetchSolicitudes();
      if (expandedId === id) {
        cargarDetalle({ id });
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al asignar.');
    }
  };

  const handleDescargarDocAdicional = (doc) => {
    window.open(`/api/v1/archivero/documentos/${doc.token_descarga}/descargar`, '_blank');
  };

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const filteredSolicitudes = useMemo(() => {
    return solicitudes.filter(sol => {
      if (filtroEstado && String(sol.estado_id) !== String(filtroEstado)) return false;
      const estadoNombre = sol.estado?.nombre || '';
      if (mostrarSoloActivas && (estadoNombre === 'Completado' || estadoNombre === 'Rechazada')) return false;
      if (mostrarSoloMias && sol.worker_id !== user.id) return false;
      
      if (searchTerm) {
        const lowerSearch = searchTerm.toLowerCase();
        if (
          !sol.nombre_solicitante?.toLowerCase().includes(lowerSearch) &&
          !sol.rut_solicitante?.toLowerCase().includes(lowerSearch) &&
          !sol.numero_seguimiento?.toLowerCase().includes(lowerSearch)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [solicitudes, filtroEstado, mostrarSoloActivas, mostrarSoloMias, searchTerm, user.id]);

  const paginatedSolicitudes = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredSolicitudes.slice(start, start + itemsPerPage);
  }, [filteredSolicitudes, currentPage]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  return (
    <div className="container" style={{ padding: '2rem clamp(16px,4vw,48px)' }}>
      {/* Header */}
      <div className="animate-fade-in" style={{ marginBottom: '1.5rem' }}>
        <h2 className="section-title">Gestión de Archivero Judicial</h2>
        <span className="accent-line" />
        <p className="section-subtitle">
          Administre las solicitudes de copias de escrituras y protocolizados.
        </p>
      </div>

      {/* Buscador y Filtros */}
      <div className="animate-fade-in" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', width: '100%', maxWidth: 440 }}>
          <Search size={17} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input type="text" placeholder="Buscar por nombre, RUT o folio (ARCH-XXXX)..."
            value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            style={{ paddingLeft: 38, borderRadius: 24, background: 'var(--bg-surface)' }} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)' }}>Estado:</span>
            <select
              value={filtroEstado}
              onChange={(e) => { setFiltroEstado(e.target.value); setCurrentPage(1); }}
              style={{
                padding: '6px 12px',
                borderRadius: '12px',
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-color)',
                fontSize: '0.85rem',
                color: 'var(--text-main)',
                minWidth: '160px'
              }}
            >
              <option value="">Todos los estados</option>
              {estados.map(est => (
                <option key={est.id} value={est.id}>{est.nombre}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px', 
              background: mostrarSoloMias ? 'var(--primary-light)' : '#f1f5f9',
              color: mostrarSoloMias ? 'white' : 'var(--text-main)',
              padding: '0.6rem 1.2rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 600,
              transition: 'all 0.2s',
              whiteSpace: 'nowrap'
            }}>
              <input 
                type="checkbox" 
                checked={mostrarSoloMias}
                onChange={(e) => { setMostrarSoloMias(e.target.checked); setCurrentPage(1); }}
                style={{ width: '16px', height: '16px' }}
              />
              Mis Solicitudes
            </label>

            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px', 
              background: mostrarSoloActivas ? '#fee2e2' : '#f1f5f9',
              color: mostrarSoloActivas ? '#991b1b' : 'var(--text-main)',
              padding: '0.6rem 1.2rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 600,
              transition: 'all 0.2s',
              whiteSpace: 'nowrap'
            }}>
              <input 
                type="checkbox" 
                checked={mostrarSoloActivas}
                onChange={(e) => { setMostrarSoloActivas(e.target.checked); setCurrentPage(1); }}
                style={{ width: '16px', height: '16px' }}
              />
              Ocultar Finalizadas
            </label>
          </div>
        </div>
      </div>

      {/* Lista */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '4rem' }}><span className="spinner spinner-lg" /></div>
      ) : filteredSolicitudes.length === 0 ? (
        <div className="glass-card" style={{ textAlign: 'center', padding: '4rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📥</div>
          <h3>Sin resultados</h3>
          <p className="text-muted">No se encontraron solicitudes con esos criterios.</p>
        </div>
      ) : (
        <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
          {paginatedSolicitudes.map(solicitud => (
            <div key={solicitud.id} className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>

              {/* Fila resumen (clic para expandir) */}
              <div
                style={{
                  padding: '0.85rem 1.2rem', display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', cursor: 'pointer',
                  background: expandedId === solicitud.id ? '#f8fafc' : 'transparent',
                }}
                onClick={() => toggleExpand(solicitud)}
              >
                <div>
                  <div style={{ display: 'flex', gap: '0.7rem', alignItems: 'center', marginBottom: '0.2rem' }}>
                    <span style={{ fontWeight: 800, color: 'var(--primary-dark)', fontSize: '0.95rem' }}>
                      {solicitud.nombre_solicitante}
                    </span>
                    <span className={`badge ${getBadgeClass(solicitud.estado?.nombre)}`}
                      style={{ textTransform: 'uppercase', padding: '2px 8px', fontSize: '0.63rem', borderRadius: 4 }}>
                      {solicitud.estado?.nombre || 'Desconocido'}
                    </span>
                    {solicitud.has_unread_reply && (
                      <span style={{ background: '#fee2e2', color: '#991b1b', padding: '2px 6px', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{width: 6, height: 6, background: '#ef4444', borderRadius: '50%', display: 'inline-block'}}></span>
                        Nueva Respuesta
                      </span>
                    )}
                    {solicitud.s3_path && (
                      <span title="Tiene documento original" style={{ color: 'var(--copper)', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Paperclip size={13} /> Orig
                      </span>
                    )}
                    {solicitud.comprobante_pago_path && (
                      <span title="Comprobante Subido" style={{ color: 'var(--success)', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 3 }}>
                        <DollarSign size={13} /> Comprobante
                      </span>
                    )}
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                    RUT: {solicitud.rut_solicitante} &nbsp;·&nbsp;
                    <span style={{ fontFamily: 'monospace' }}>{solicitud.numero_seguimiento || 'Sin número'}</span> &nbsp;·&nbsp;
                    {new Date(solicitud.created_at).toLocaleDateString('es-CL')}
                    {solicitud.worker
                      ? <span style={{ marginLeft: 6, color: '#b87333', fontWeight: 600 }}>· 👤 {solicitud.worker.name}</span>
                      : <span style={{ marginLeft: 6, color: 'var(--text-muted)', fontStyle: 'italic' }}>· Sin asignar</span>
                    }
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-main)', fontWeight: 600 }}>
                    {solicitud.tipo_solicitud === 'escritura' ? 'Copia Escritura' : 'Protocolizado'}
                  </span>
                  {expandedId === solicitud.id ? <ChevronUp size={18} color="var(--primary)" /> : <ChevronDown size={18} color="var(--primary)" />}
                </div>
              </div>

              {/* Panel expandido */}
              {expandedId === solicitud.id && (
                <div style={{ borderTop: '1px solid var(--border-color)', background: '#f8fafc' }}>
                  
                  {/* Acciones Rápidas */}
                  <div style={{ padding: '1rem 1.2rem', display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center', borderBottom: '1px solid var(--border-color)' }}>
                    <div style={{ fontSize: '0.83rem' }}>
                      <strong>Email:</strong> {solicitud.email} &nbsp;
                      {solicitud.telefono && <><strong>Tel:</strong> {solicitud.telefono} &nbsp;</>}
                    </div>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {/* Asignar funcionario — solo boss+ */}
                      {canAssign && (
                        <select
                          value={solicitud.worker_id || ''}
                          onChange={e => handleAsignar(solicitud.id, e.target.value)}
                          style={{ padding: '5px 10px', fontSize: '0.82rem', borderRadius: 6, borderColor: solicitud.worker_id ? '#b87333' : 'var(--border-color)', color: solicitud.worker_id ? '#b87333' : 'inherit', fontWeight: solicitud.worker_id ? 600 : 400 }}
                        >
                          <option value="">👤 Sin asignar</option>
                          {usuarios.map(u => (
                            <option key={u.id} value={u.id}>{u.name}</option>
                          ))}
                        </select>
                      )}
                      
                      {/* Cambiar estado */}
                      <select value={solicitud.estado_id || ''}
                        onChange={e => cambiarEstado(solicitud.id, e.target.value)}
                        style={{ padding: '5px 28px 5px 10px', fontSize: '0.82rem', borderRadius: 6 }}>
                        <option value="" disabled>Cambiar Estado...</option>
                        {estados.map(est => (
                          <option key={est.id} value={est.id}>{est.nombre}</option>
                        ))}
                      </select>

                      <button className="btn btn-sm"
                        style={{ background: '#b87333', color: '#fff', display: 'flex', alignItems: 'center', gap: 5, border: 'none' }}
                        onClick={() => navigate(`/archivero/historial/${solicitud.id}`, { state: { from: '/gestion-archivero' } })}>
                        <Eye size={14} /> Observar Historial Completo
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                    {/* COLUMNA IZQUIERDA: Detalle del Archivero */}
                    <div style={{ padding: '1rem 1.2rem', borderRight: '1px solid var(--border-color)' }}>
                      <h4 style={{ margin: '0 0 0.8rem', fontSize: '0.88rem', color: 'var(--primary-dark)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Archive size={15} /> Detalle del Documento Solicitado
                      </h4>
                      <div className="table-wrapper">
                        <table style={{ background: 'var(--bg-surface)', fontSize: '0.8rem' }}>
                          <tbody>
                            <tr>
                              <td style={{ fontWeight: 600, width: '40%' }}>Notaría</td>
                              <td>{solicitud.notaria === 'primera_san_carlos' ? 'Primera San Carlos' : 'Segunda San Carlos'}</td>
                            </tr>
                            <tr>
                              <td style={{ fontWeight: 600 }}>Año</td>
                              <td>{solicitud.anio || solicitud.fecha}</td>
                            </tr>
                            {solicitud.tipo_solicitud === 'escritura' ? (
                              <>
                                <tr>
                                  <td style={{ fontWeight: 600 }}>Repertorio</td>
                                  <td>{solicitud.repertorio}</td>
                                </tr>
                                <tr>
                                  <td style={{ fontWeight: 600 }}>Vigencia</td>
                                  <td>{solicitud.con_vigencia ? <span className="badge badge-warning">Con Vigencia</span> : 'Sin Vigencia'}</td>
                                </tr>
                              </>
                            ) : (
                              <>
                                <tr>
                                  <td style={{ fontWeight: 600 }}>Nº Protocolización</td>
                                  <td>{solicitud.numero_protocolizacion}</td>
                                </tr>
                                {solicitud.repertorio && (
                                  <tr>
                                    <td style={{ fontWeight: 600 }}>Repertorio</td>
                                    <td>{solicitud.repertorio}</td>
                                  </tr>
                                )}
                              </>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* COLUMNA DERECHA: Documentos */}
                    <div style={{ padding: '1rem 1.2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {/* Documento original del ciudadano */}
                      <div>
                        <h4 style={{ margin: '0 0 0.6rem', fontSize: '0.88rem', color: 'var(--primary-dark)', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Paperclip size={15} /> Documento Adjunto Ciudadano
                        </h4>
                        {!solicitud.s3_path && (!solicitud.documentos || solicitud.documentos.filter(d => !d.subido_por).length === 0) ? (
                          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>No adjuntó documento al enviar la solicitud.</p>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {solicitud.s3_path && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                                <FileText size={16} color="var(--success)" />
                                <span style={{ fontSize: '0.82rem', fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {solicitud.nombre_archivo_original || 'Documento Original'}
                                </span>
                                <a href={`/api/v1/archivero/solicitudes/${solicitud.id}/documento`} target="_blank" rel="noreferrer"
                                  style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', color: 'var(--copper)', fontWeight: 600 }}>
                                  <Download size={14} /> Descargar
                                </a>
                              </div>
                            )}
                            {solicitud.documentos && solicitud.documentos.filter(d => !d.subido_por).map(doc => (
                              <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                                <FileText size={16} color="var(--success)" />
                                <span style={{ fontSize: '0.82rem', fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {doc.nombre_original || 'Documento Adicional'}
                                </span>
                                <a href={`/api/v1/archivero/documentos/${doc.token_descarga}/descargar`} target="_blank" rel="noreferrer"
                                  style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', color: 'var(--copper)', fontWeight: 600 }}>
                                  <Download size={14} /> Descargar
                                </a>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Comprobante de Pago */}
                      {solicitud.comprobante_pago_path && (
                        <div>
                          <h4 style={{ margin: '0 0 0.6rem', fontSize: '0.88rem', color: 'var(--primary-dark)', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <DollarSign size={15} /> Comprobante de Pago
                          </h4>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', background: '#e0e7ff', borderRadius: 8, border: '1px solid #c7d2fe' }}>
                            <FileText size={16} color="var(--primary)" />
                            <span style={{ fontSize: '0.82rem', fontWeight: 600, flex: 1 }}>
                              Comprobante de Pago
                            </span>
                            <a href={`/api/v1/archivero/solicitudes/${solicitud.id}/documento?type=comprobante`} target="_blank" rel="noreferrer"
                              style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', color: 'var(--primary)', fontWeight: 600 }}>
                              <Download size={14} /> Descargar
                            </a>
                          </div>
                        </div>
                      )}

                      {/* Documentos adicionales (staff) */}
                      <div>
                        <h4 style={{ margin: '0 0 0.6rem', fontSize: '0.88rem', color: 'var(--primary-dark)', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Upload size={15} /> Documentos Adicionales
                        </h4>

                        {/* Lista de docs existentes */}
                        {!expandedDetail[solicitud.id] ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                            <span className="spinner" style={{ width: 16, height: 16 }} /> Cargando...
                          </div>
                        ) : expandedDetail[solicitud.id].documentos?.length > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.6rem' }}>
                            {expandedDetail[solicitud.id].documentos.map(doc => (
                              <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.7rem', background: '#fff', borderRadius: 7, border: '1px solid var(--border-color)', fontSize: '0.8rem' }}>
                                <FileText size={13} color="var(--copper)" />
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.nombre_original}</span>
                                <button className="icon-btn" onClick={() => handleDescargarDocAdicional(doc)} title="Descargar" style={{ padding: '4px 8px' }}>
                                  <Download size={13} />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>Sin documentos adicionales.</p>
                        )}

                        {/* Botón para abrir modal Subir Documentos */}
                        <div style={{ marginTop: '0.5rem' }}>
                          <button 
                            className="btn btn-sm"
                            style={{ 
                              background: '#f8fafc', 
                              color: 'var(--primary-dark)', 
                              border: '1px solid var(--border-color)', 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 6,
                              padding: '0.5rem 1rem'
                            }}
                            onClick={() => setModalSubirDocs(solicitud)}
                          >
                            <Upload size={15} /> Subir Archivos
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── SECCIÓN DE MENSAJES Y COMUNICACIÓN ── */}
                  <div className="glass-card animate-fade-in" style={{ marginTop: '1.5rem', padding: '1.5rem', background: '#fff', border: '1px solid var(--border-color)', borderRadius: '12px' }}>
                    <h3 style={{ fontSize: '1rem', color: 'var(--primary-dark)', marginBottom: '1.2rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <MessageSquare size={18} color="var(--primary)" /> Mensajes y Notificaciones
                    </h3>

                    {/* Hilo de mensajes */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem', maxHeight: '400px', overflowY: 'auto', paddingRight: '0.5rem' }}>
                      {expandedDetail[solicitud.id]?.workflow_steps?.length > 0 ? (
                        expandedDetail[solicitud.id].workflow_steps.filter(s => s.is_public).map((step) => {
                          const isFuncionario = !!step.worker_id;
                          const isPago = step.payload?.tipo === 'comprobante_pago';
                          
                          return (
                            <div key={step.id} style={{
                              alignSelf: isFuncionario ? 'flex-end' : 'flex-start',
                              maxWidth: '85%',
                              background: isFuncionario ? 'var(--primary)' : '#f3f4f6',
                              color: isFuncionario ? '#ffffff' : '#374151',
                              padding: '0.8rem 1rem',
                              borderRadius: '12px',
                              borderTopLeftRadius: isFuncionario ? '12px' : '2px',
                              borderTopRightRadius: isFuncionario ? '2px' : '12px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', gap: '1rem' }}>
                                <span style={{ fontSize: '0.72rem', fontWeight: 700, opacity: 0.8 }}>
                                  {isFuncionario ? 'Tú (Funcionario)' : 'Ciudadano'}
                                </span>
                                <span style={{ fontSize: '0.65rem', opacity: 0.6 }}>
                                  {new Date(step.created_at).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' })}
                                </span>
                              </div>
                              
                              {isPago && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: isFuncionario ? '#d1fae5' : 'var(--success)', fontSize: '0.75rem', fontWeight: 700, marginBottom: 4 }}>
                                  <DollarSign size={12} /> Comprobante de Pago Subido
                                </div>
                              )}

                              {step.payload?.asunto && (
                                <div style={{ fontWeight: 700, fontSize: '0.82rem', marginBottom: 2 }}>{step.payload.asunto}</div>
                              )}

                              <div style={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                                {step.payload?.mensaje || step.payload?.comentario || step.payload?.nota || step.description}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-muted" style={{ textAlign: 'center', fontSize: '0.85rem', padding: '1rem' }}>
                          No hay mensajes todavía.
                        </p>
                      )}
                    </div>

                    {/* Responder */}
                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '1.5rem' }}>
                      <h4 style={{ fontSize: '0.9rem', marginBottom: '0.8rem', color: 'var(--primary-dark)' }}>Responder o enviar duda:</h4>
                      <form onSubmit={(e) => handleEnviarMensaje(e, solicitud.id)} style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                        <textarea 
                          rows={3} 
                          className="form-control" 
                          placeholder="Escribe tu mensaje aquí..." 
                          value={respuestas[solicitud.id] || ''} 
                          onChange={e => setRespuestas(prev => ({ ...prev, [solicitud.id]: e.target.value }))} 
                          style={{ fontSize: '0.85rem', background: '#f8fafc' }} 
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <button type="submit" className="btn btn-primary btn-sm" disabled={enviandoMensaje[solicitud.id] || !(respuestas[solicitud.id] || '').trim()} style={{ borderRadius: '20px', padding: '0.5rem 1.2rem' }}>
                            {enviandoMensaje[solicitud.id] ? <span className="spinner" style={{ width: 14, height: 14 }}/> : <><Send size={14} style={{ marginRight: 6 }}/> Enviar Respuesta</>}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>

                </div>
              )}
            </div>
          ))}

          {/* Paginación */}
          {filteredSolicitudes.length > 0 && (
            <Pagination 
              currentPage={currentPage} 
              totalItems={filteredSolicitudes.length} 
              itemsPerPage={itemsPerPage} 
              onPageChange={handlePageChange} 
            />
          )}
        </div>
      )}
      {/* Modal Subir Documentos */}
      {modalSubirDocs && (
        <SubirDocumentosModal
          solicitud={expandedDetail[modalSubirDocs.id] || modalSubirDocs}
          isArchivero={true}
          onClose={() => setModalSubirDocs(null)}
          onSuccess={() => {
            setModalSubirDocs(null);
            cargarDetalle(modalSubirDocs);
          }}
        />
      )}
    </div>
  );
}
